/**
 * The document line table — `Record/LineItemsTable` (12:7841), the ONE quote
 * artefact Figma actually authored.
 *
 * The design draws it as a tab body inside the INQUIRY record and this phase
 * cannot put it there (an inquiry does not own a quote), so it is built here as
 * the shared body of three screens: the quote record, the sales-order record, and
 * the builder's priced preview. All three render server-computed lines and none
 * of them may edit one, which is why this component takes no callbacks at all.
 *
 * WHAT THE DESIGN SHOWS AND THIS KEEPS: the column set (item, qty, rate,
 * discount, line total), right-aligned numerics, a `TableHeader/11` header row,
 * a rule between rows, and the totals strip beneath (built separately, in
 * `money.tsx`).
 *
 * WHAT THE DESIGN SHOWS AND THIS DOES NOT BUILD: the "Line total vs Quoted"
 * RECONCILIATION strip. It compares a computed line sum against a separately
 * quoted figure — two numbers that, in this system, are the same number. §9.1
 * produces `line_total` and `subtotal` from one pass, so a reconciliation row
 * could only ever say "0.00 difference", every time, forever. Rendering it would
 * imply the app is checking something. It is not; the server is, once.
 *
 * EVERY CELL IS A STRING FROM THE WIRE. See `money.tsx`.
 */

import type { ReactNode } from 'react';
import { ServerAmount, ServerQuantity } from './money';
import { cn } from '../../lib/cn';

/**
 * The shape all three callers share.
 *
 * `QuoteLineRowDto`, `SoLineRowDto` and `PreviewLineDto` are three different API
 * types with these fields in common. Structural typing means each is assignable
 * here without a mapper, and writing the narrow shape rather than a union keeps
 * this component unable to reach for a field only one of them has.
 */
export interface DocumentLine {
  line_no: number;
  product_code: string | null;
  description: string;
  qty: string;
  users?: number | null;
  months?: number | null;
  unit_rate: string;
  discount_pct: string;
  line_total: string;
}

/* =============================================================================
 * LINK COLOUR — A MEASURED DEPARTURE FROM THE APP'S EXISTING PATTERN
 * =============================================================================
 * Every navigational link on the five screens in this feature is `text/primary`
 * with a permanent UNDERLINE, not `accent/indigo`.
 *
 * MEASURED: `accent/indigo` #5b6cfa on `bg/surface` is **4.22:1 in light and
 * 3.90:1 in dark** — below the 4.5:1 floor for the 12–13px text these links
 * carry, in BOTH modes. The app already uses `text-indigo hover:underline` for
 * links on the inquiry screens; that is a PRE-EXISTING pair, it is reported
 * rather than rewritten (rewriting it is a shell-wide change with its own
 * before/after evidence), and it is not REPLICATED here. Adding five more
 * screens' worth of a failing pair because the failing pair already exists is
 * how a defect becomes a convention.
 *
 * `text/primary` measures 17.04:1 light and 15.11:1 dark, and the underline
 * carries the affordance — which also satisfies WCAG 1.4.1: the link is not
 * distinguished by colour alone. Hover thickens the rule instead of changing
 * hue, so the resting and hover states are the same measured pair.
 * ========================================================================== */

/** `TableHeader/11` — 11/14 medium, +0.5px, uppercase, on the plain surface. */
const HEADER_CELL = 'px-4 py-2.5 text-th uppercase text-fg-secondary';
const BODY_CELL = 'px-4 py-3 align-top text-body13 text-fg';

export function DocumentLineTable({
  lines,
  currency,
  /**
   * Optional per-line annotation, keyed by `line_no` — the OQ-1 refusal marker on
   * the builder. `null`/absent renders nothing, so the record pages are
   * byte-identical to a table with no notice machinery at all.
   */
  noticeFor,
  caption,
  frameless = false,
}: {
  lines: readonly DocumentLine[];
  currency: string | null | undefined;
  noticeFor?: (line: DocumentLine) => ReactNode;
  /** Visually-hidden table caption. Screen readers need to know which table this is. */
  caption: string;
  /**
   * WT4 — drop the component's own card frame (border + radius + surface) so
   * the table can sit flush inside a `RecordCard` per `record.html`'s lines
   * card (card header → table → totals footer, ONE card). Default keeps the
   * framed shape every pre-Tier-A caller renders; additive, no caller moved.
   */
  frameless?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={
        frameless
          ? 'overflow-x-auto'
          : 'overflow-x-auto rounded-md border border-line bg-surface'
      }
    >
      <table className="w-full min-w-[640px] border-collapse">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className={cn(HEADER_CELL, 'w-10 text-left')}>
              #
            </th>
            <th scope="col" className={cn(HEADER_CELL, 'text-left')}>
              Item
            </th>
            <th scope="col" className={cn(HEADER_CELL, 'text-right')}>
              Qty
            </th>
            <th scope="col" className={cn(HEADER_CELL, 'text-right')}>
              Rate
            </th>
            <th scope="col" className={cn(HEADER_CELL, 'text-right')}>
              Disc %
            </th>
            <th scope="col" className={cn(HEADER_CELL, 'text-right')}>
              Line total
            </th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const notice = noticeFor?.(line) ?? null;
            const subscription =
              (line.users ?? null) !== null || (line.months ?? null) !== null;
            return (
              <tr key={line.line_no} className="border-b border-line last:border-b-0">
                <td className={cn(BODY_CELL, 'tnum text-fg-secondary')}>{line.line_no}</td>
                <td className={BODY_CELL}>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-fg">{line.description}</span>
                    {line.product_code === null ? null : (
                      <span className="font-mono text-mono text-fg-secondary">
                        {line.product_code}
                      </span>
                    )}
                    {subscription ? (
                      <span className="text-label text-fg-secondary">
                        {/* Captured on the line, and — OQ-1 — NOT yet part of any
                          * formula. Shown because the operator typed it and it is
                          * stored; the totals do not reflect it and the builder's
                          * own notice says so. */}
                        {(line.users ?? null) === null
                          ? ''
                          : `${String(line.users)} user${line.users === 1 ? '' : 's'}`}
                        {(line.users ?? null) !== null && (line.months ?? null) !== null
                          ? ' · '
                          : ''}
                        {(line.months ?? null) === null
                          ? ''
                          : `${String(line.months)} month${line.months === 1 ? '' : 's'}`}
                      </span>
                    ) : null}
                    {notice}
                  </div>
                </td>
                <td className={cn(BODY_CELL, 'text-right')}>
                  <ServerQuantity value={line.qty} />
                </td>
                <td className={cn(BODY_CELL, 'text-right')}>
                  <ServerAmount value={line.unit_rate} currency={currency} />
                </td>
                <td className={cn(BODY_CELL, 'text-right')}>
                  <ServerQuantity value={line.discount_pct} suffix="%" />
                </td>
                <td className={cn(BODY_CELL, 'text-right font-medium')}>
                  <ServerAmount value={line.line_total} currency={currency} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
